import type { LogbookEntry } from '@/lib/db';

import { caa1373Row, type CaaCapacity, type CaaEngineGroup } from './caa1373';
import {
  columnGroupRuns,
  type ColumnGroupRun,
  ENGINE_CLASS_SHORT,
  entryTotalHours,
  formatIntermediate,
  type LogbookColumn,
} from './columns';
import { formatDecimalHours, roundDecimalHours } from './time';

/**
 * CAA Form 1373, as a table.
 *
 * The third of the three layers: `LOGBOOK_COLUMNS` is what a pilot types
 * into, `caa1373.ts` turns those facts into the form's categories, and this
 * is the form itself — the column order, the grouped headings and the
 * wording a NZ pilot already knows from the paper page.
 *
 * Every column here is read-only, because every column here is a
 * consequence. There is no cell in this table that is the only place some
 * number lives, which is what makes the printed page and the entry screen
 * incapable of disagreeing.
 *
 * These reuse `LogbookColumn` rather than a type of their own so the grid,
 * the print view and the CSV exporter can render either table with the same
 * code — a view toggle rather than a second table implementation.
 */

const FLIGHT = 'Flight Information';
const TOTAL = 'Total';
const SINGLE = 'Single Engine';
const MULTI = 'Multi Engine';
const INSTRUMENT = 'Instrument';
const OTHER = 'Other';
const TAKEOFF = 'Take-offs';
const LAND = 'Landings';

const DAY = 'Day';
const NIGHT = 'Night';

function textColumn(
  id: string,
  label: string,
  width: string,
  read: (entry: LogbookEntry) => string,
): LogbookColumn {
  return {
    id,
    label,
    group: 'flight',
    groupLabel: FLIGHT,
    align: 'left',
    width,
    readOnly: true,
    read,
    write: (entry) => entry,
  };
}

function hoursColumn(
  id: string,
  label: string,
  group: LogbookColumn['group'],
  groupLabel: string,
  hours: (entry: LogbookEntry) => number,
  subGroupLabel?: string,
): LogbookColumn {
  return {
    id,
    label,
    group,
    groupLabel,
    ...(subGroupLabel === undefined ? {} : { subGroupLabel }),
    align: 'right',
    width: 'w-16',
    readOnly: true,
    // A blank box, not `0.00`, exactly as a paper page leaves it: sixteen
    // zeroes across a row is noise the eye has to filter before it can find
    // the one number that matters.
    read: (entry) => {
      const value = hours(entry);
      return value === 0 ? '' : formatDecimalHours(value);
    },
    total: (entries) => {
      const sum = roundDecimalHours(entries.reduce((running, e) => running + hours(e), 0));
      return sum === 0 ? '' : formatDecimalHours(sum);
    },
    write: (entry) => entry,
  };
}

function countColumn(
  id: string,
  label: string,
  group: LogbookColumn['group'],
  groupLabel: string,
  count: (entry: LogbookEntry) => number,
): LogbookColumn {
  return {
    id,
    label,
    group,
    groupLabel,
    align: 'right',
    width: 'w-12',
    readOnly: true,
    read: (entry) => {
      const value = count(entry);
      return value === 0 ? '' : String(value);
    },
    total: (entries) => {
      const sum = entries.reduce((running, e) => running + count(e), 0);
      return sum === 0 ? '' : String(sum);
    },
    write: (entry) => entry,
  };
}

/** One SE/ME × day/night × capacity cell. */
function capacityColumn(
  engineGroup: CaaEngineGroup,
  capacity: CaaCapacity,
  label: string,
  band: typeof DAY | typeof NIGHT,
): LogbookColumn {
  const side = band === DAY ? 'day' : 'night';
  return hoursColumn(
    `caa.${engineGroup}.${side}.${capacity}`,
    label,
    engineGroup === 'single' ? 'single-engine' : 'multi-engine',
    engineGroup === 'single' ? SINGLE : MULTI,
    (entry) => caa1373Row(entry)[engineGroup][capacity][side],
    band,
  );
}

/** The labels 1373 prints inside each engine bank, in its order. */
const SINGLE_CAPACITIES: readonly [CaaCapacity, string][] = [
  ['dual', 'Dual'],
  ['pic', 'PIC'],
];
const MULTI_CAPACITIES: readonly [CaaCapacity, string][] = [
  ['dual', 'Dual'],
  ['pic', 'PIC'],
  ['coPilot', 'Co-Pilot'],
  ['commandPractice', 'Command Practice'],
];

/** Display wording for `FlightPurpose`, in the order the entry form offers them. */
export const PURPOSE_LABEL = {
  general: 'General',
  training: 'Training',
  'flight-test': 'Flight Test',
  'flight-review': 'Flight Review',
  currency: 'Currency',
  ferry: 'Ferry',
  'cross-country': 'Cross Country',
  ifr: 'IFR',
  other: 'Other',
} as const;

/**
 * What goes in the DETAILS OF FLIGHT column.
 *
 * The pilot has already said what kind of flight this was and, for training,
 * what was practised; §21 of the redesign brief is explicit that they should
 * not then type it again into a free-text box. So the structured fields are
 * composed into the column the form prints, with the pilot's own notes last
 * and never replaced by the composition.
 *
 * A row where nothing structured was recorded reads exactly as its remarks,
 * which is what an entry transcribed from a paper logbook should look like.
 */
export function detailsOfFlight(entry: LogbookEntry): string {
  const parts: string[] = [];
  if (entry.purpose !== undefined && entry.purpose !== 'general') {
    parts.push(PURPOSE_LABEL[entry.purpose]);
  }
  const training = entry.trainingDetails?.trim();
  if (training !== undefined && training !== '') {
    parts.push(training);
  }
  const remarks = entry.remarks?.trim();
  if (remarks !== undefined && remarks !== '') {
    parts.push(remarks);
  }
  return parts.join(' – ');
}

export const CAA_1373_COLUMNS: readonly LogbookColumn[] = [
  textColumn('date', 'Date', 'w-24', (entry) => entry.date),
  textColumn('from', 'From', 'w-16', (entry) => entry.from ?? ''),
  textColumn('intermediate', 'Intermediate', 'w-28', (entry) =>
    formatIntermediate(entry.intermediate),
  ),
  textColumn('to', 'To', 'w-16', (entry) => entry.to ?? ''),
  textColumn('aircraftType', 'Aircraft Type', 'w-20', (entry) => entry.aircraftType ?? ''),
  // The class rides along with the registration rather than taking a column
  // the form does not have: it is what decides which engine bank the hours
  // below print under, and a reader has to be able to see that decision.
  textColumn('registration', 'Registration', 'w-24', (entry) =>
    entry.registration === undefined
      ? ''
      : `${entry.registration} (${ENGINE_CLASS_SHORT[entry.time.engineClass]})`,
  ),
  textColumn('pic', 'PIC', 'w-24', (entry) => entry.pic),
  textColumn('otherCrew', 'Co-Pilot / Student', 'w-24', (entry) => entry.otherCrew ?? ''),
  textColumn('remarks', 'Details of Flight', 'w-48', detailsOfFlight),
  hoursColumn('time.totalHr', 'Total Flight Time', 'total', TOTAL, entryTotalHours),
  ...SINGLE_CAPACITIES.map(([capacity, label]) => capacityColumn('single', capacity, label, DAY)),
  ...SINGLE_CAPACITIES.map(([capacity, label]) => capacityColumn('single', capacity, label, NIGHT)),
  ...MULTI_CAPACITIES.map(([capacity, label]) => capacityColumn('multi', capacity, label, DAY)),
  ...MULTI_CAPACITIES.map(([capacity, label]) => capacityColumn('multi', capacity, label, NIGHT)),
  hoursColumn(
    'time.actualInstrumentHr',
    'Actual',
    'instrument',
    INSTRUMENT,
    (entry) => entry.time.actualInstrumentHr,
  ),
  hoursColumn(
    'time.simulatedInstrumentHr',
    'Simulated',
    'instrument',
    INSTRUMENT,
    (entry) => entry.time.simulatedInstrumentHr,
  ),
  hoursColumn(
    'time.groundInstrumentHr',
    'Ground',
    'instrument',
    INSTRUMENT,
    (entry) => entry.time.groundInstrumentHr,
  ),
  countColumn(
    'approaches.nonPrecision',
    'Non-Precision Approaches',
    'instrument',
    INSTRUMENT,
    (entry) => entry.approaches.nonPrecision,
  ),
  countColumn(
    'approaches.precision',
    'Precision Approaches',
    'instrument',
    INSTRUMENT,
    (entry) => entry.approaches.precision,
  ),
  hoursColumn(
    'time.instructorHr',
    'Flight Instructor',
    'other',
    OTHER,
    (entry) => entry.time.instructorHr,
  ),
  hoursColumn(
    'time.ifrCrossCountryHr',
    'IFR Cross Country',
    'other',
    OTHER,
    (entry) => entry.time.ifrCrossCountryHr,
  ),
  countColumn('takeoffs.day', 'Day', 'takeoffs', TAKEOFF, (entry) => entry.takeoffs.day),
  countColumn('takeoffs.night', 'Night', 'takeoffs', TAKEOFF, (entry) => entry.takeoffs.night),
  countColumn('landings.day', 'Day', 'landings', LAND, (entry) => entry.landings.day),
  countColumn('landings.night', 'Night', 'landings', LAND, (entry) => entry.landings.night),
];

/** The top header band — SINGLE ENGINE, MULTI ENGINE, INSTRUMENT and the rest. */
export const CAA_1373_GROUPS: readonly ColumnGroupRun[] = columnGroupRuns(
  CAA_1373_COLUMNS,
  (column) => column.groupLabel,
);

/** The DAY/NIGHT band inside the two engine groups; blank above everything else. */
export const CAA_1373_SUBGROUPS: readonly ColumnGroupRun[] = columnGroupRuns(
  CAA_1373_COLUMNS,
  (column) => column.subGroupLabel,
);
